import React, { useEffect } from "react"
import { useLocation, Navigate } from "react-router-dom"
import userService from "../services/users/userService"
import authService from "../services/authService"

// Mapping of routes to titles
export const routeTitles: { [key: string]: string } = {
    "/": "Welcome to POSNex",
    "/home": "Home",
    "/admin": "Dashboard",
    "/comptable": "Comptable Dashboard",
    "/cashier": "Cashier Dashboard",
    "/cashier/pos": "Point of Sale",
    "/cashier/refunds": "Refunds",
    "/cashier/sales": "Sales",
    "/cashier/clients": "Clients",
    "/admin/cuisinier": "Kitchen Dashboard",
    "/admin/restaurant/kitchen": "Kitchen Management",
    "/admin/entities/products": "Products",
    "/admin/entities/category": "Categories",
    "/admin/entities/subcategory": "Subcategories",
    "/admin/entities/brand": "Brands",
    "/admin/entities/shipping": "Shipping",
    "/admin/entities/clients": "Clients",
    "/admin/cash-register/pos": "Point of Sale",
    "/admin/cash-register/sales": "Sales",
    "/admin/cash-register/refunds": "Refunds",
    "/admin/restaurant/tables": "Restaurant Tables",
    "/admin/restaurant/positions": "Restaurant Positions",
    "/admin/store/orders": "Orders",
    "/admin/stock/management": "Stock Management",
    "/admin/stock/inventory": "Inventory",
    "/admin/stock/suppliers": "Suppliers",
    "/admin/users": "Users",
    "/admin/settings": "Settings",
    "/admin/comptability/statistics": "Statistics",
    "/admin/comptability/accounting": "Accounting",
    "/comptable/stock/management": "Stock Management",
    "/comptable/stock/inventory": "Inventory",
    "/comptable/stock/suppliers": "Suppliers",
    "/comptable/comptability/statistics": "Statistics",
    "/comptable/comptability/accounting": "Accounting",
    "/admin/issues": "Issues & Support",
    "/admin/issues/list": "Problems List",
}

// Dynamic title hook
export const useDynamicTitle = () => {
    const location = useLocation()

    useEffect(() => {
        // Find the most specific matching route title
        const matchingRoute = Object.keys(routeTitles)
            .filter((route) => location.pathname.startsWith(route))
            .sort((a, b) => b.length - a.length)[0]

        const title = matchingRoute ? `${routeTitles[matchingRoute]} | POSNex` : "Store Management"

        document.title = title
    }, [location.pathname])
}

// Role-based route wrapper
export const RoleBasedRoute = ({
                                   element,
                                   allowedRoles = [],
                               }: {
    element: React.ReactElement
    allowedRoles?: string[]
}) => {
    // Get current user role
    const currentRole = userService.getRoleFromToken()?.toLowerCase()

    // If no roles specified, allow all authenticated users
    if (allowedRoles.length === 0) {
        return element
    }

    // Check if current role is allowed (case-insensitive comparison)
    const isAllowed = currentRole && allowedRoles.map(role => role.toLowerCase()).includes(currentRole)

    // If not allowed, return restricted access
    if (!isAllowed) {
        return React.createElement(Navigate, {
            to: "/restricted-access",
            replace: true,
            state: {
                requiredRole: allowedRoles.join(" or "),
                currentRole: currentRole || "Unknown"
            }
        })
    }

    // If allowed, render the route
    return element
}

// After login, redirect based on role
export const LoginRedirect = () => {
    const baseRoute = authService.getBaseRouteForRole()
    return React.createElement(Navigate, { to: baseRoute, replace: true })
}

// Cashier route guard - allows cashiers and admin roles
export const CashierRouteGuard = ({ children }: { children: React.ReactNode }) => {
    const currentRole = userService.getRoleFromToken()?.toLowerCase()

    // Apply dynamic title (always call hook regardless of conditions)
    useDynamicTitle()

    // Define allowed roles for cashier routes
    const allowedRoles = ['caissier', 'vendeur', 'admin', 'manager', 'owner']

    // If not an allowed role, redirect to appropriate dashboard
    if (!currentRole || !allowedRoles.includes(currentRole)) {
        const baseRoute = authService.getBaseRouteForRole()
        return React.createElement(Navigate, { to: baseRoute, replace: true })
    }

    return React.createElement(React.Fragment, null, children)
}

// Cuisinier route guard - allows kitchen staff and admin roles
export const CuisinierRouteGuard = ({ children }: { children: React.ReactNode }) => {
    const currentRole = userService.getRoleFromToken()?.toLowerCase()

    // Apply dynamic title (always call hook regardless of conditions)
    useDynamicTitle()

    // Define allowed roles for kitchen routes
    const allowedRoles = ['cuisinier', 'chef', 'admin', 'manager', 'owner']

    // If not an allowed role, redirect to restricted access
    if (!currentRole || !allowedRoles.includes(currentRole)) {
        return React.createElement(Navigate, { 
            to: "/restricted-access", 
            replace: true,
            state: {
                requiredRole: "cuisinier, chef, or admin",
                currentRole: currentRole || "Unknown"
            }
        })
    }

    return React.createElement(React.Fragment, null, children)
}